import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { X, RotateCcw, Clapperboard, PartyPopper } from "lucide-react";
import { ShortSlide } from "../components/ShortSlide";
import { ReportDialog } from "../components/ReportDialog";
import { toast } from "sonner";
import { kidApi, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Shorts() {
  const { kid } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [videos, setVideos] = useState(null);
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const [report, setReport] = useState(null);
  const [saved, setSaved] = useState(new Set());
  const scroller = useRef(null);
  const isFavList = params.get("list") === "favorites";

  const toggleSave = useCallback(async (id) => {
    const on = !saved.has(id);
    setSaved((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });
    try {
      if (on) await kidApi.post(`/kid/favorites/${id}`);
      else await kidApi.delete(`/kid/favorites/${id}`);
      toast.success(on ? "Saved to your Favorites!" : "Removed from Favorites");
    } catch (e) { toast.error(errMsg(e)); }
  }, [saved]);

  const goTo = useCallback((i, behavior = "smooth") => {
    scroller.current?.children[i]?.scrollIntoView({ behavior, block: "start" });
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    kidApi.get("/kid/favorites").then((r) => setSaved(new Set(r.data.map((v) => v.id)))).catch(() => {});
    kidApi.get(isFavList ? "/kid/favorites" : "/kid/feed").then((r) => {
      setVideos(r.data);
      const i = r.data.findIndex((v) => v.id === params.get("start"));
      if (i > 0) setTimeout(() => goTo(i, "instant"), 0);
    }).catch(() => setVideos([]));
    return () => { document.body.style.overflow = ""; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!videos?.length) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(Number(e.target.dataset.index))),
      { root: scroller.current, threshold: 0.6 }
    );
    [...scroller.current.children].forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [videos]);

  useEffect(() => {
    const onKey = (e) => {
      if (report) return;
      if (e.key === "ArrowDown") { e.preventDefault(); goTo(active + 1); }
      if (e.key === "ArrowUp") { e.preventDefault(); goTo(active - 1); }
      if (e.key === "Escape") nav("/kid");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, goTo, nav, report]);

  return (
    <div data-testid="shorts-feed" className="fixed inset-0 z-50 bg-[#1B1A2E]">
      <div className="absolute top-0 inset-x-0 z-10 pt-[env(safe-area-inset-top)] bg-gradient-to-b from-black/60 to-transparent">
        <div className="h-16 px-4 flex items-center justify-between text-white">
          <span className="font-display text-2xl font-bold drop-shadow">{isFavList ? "Favorites" : "Shorts"}</span>
          {videos?.length > 0 && <span data-testid="shorts-counter" className="font-display text-sm opacity-80">{Math.min(active + 1, videos.length)} / {videos.length}</span>}
          <button data-testid="shorts-close-btn" onClick={() => nav("/kid")} aria-label="Close shorts" className="w-11 h-11 grid place-items-center rounded-full bg-white text-[#1B1A2E] border-[3px] border-[#1B1A2E]"><X size={20} /></button>
        </div>
      </div>
      {videos === null && <div className="h-full grid place-items-center text-white font-display text-xl">Loading shorts…</div>}
      {videos?.length === 0 && (
        <div data-testid="shorts-empty" className="h-full grid place-items-center text-center text-white px-8">
          <div><p className="font-display text-3xl font-bold mb-4">{isFavList ? "No favorites yet!" : "No shorts yet!"}</p>{isFavList ? <p className="mb-6 opacity-80">Tap the bookmark on any Short to save it here.</p> : null}<Link to={isFavList ? "/kid/shorts" : "/kid/studio"} className="zn-btn bg-[#FFD23F] text-[#1B1A2E]"><Clapperboard size={18} /> {isFavList ? "Browse Shorts" : "Make the first one"}</Link></div>
        </div>
      )}
      {videos?.length > 0 && (
        <div ref={scroller} data-testid="shorts-scroller" className="h-full overflow-y-scroll snap-y snap-mandatory no-scrollbar overscroll-contain">
          {videos.map((v, i) => (
            <ShortSlide key={v.id} video={v} index={i} active={i === active} near={Math.abs(i - active) <= 1} muted={muted} setMuted={setMuted}
              onEnded={() => goTo(i + 1)} onReport={(id) => setReport({ type: "video", id })} kidId={kid.id} saved={saved.has(v.id)} onToggleSave={toggleSave} />
          ))}
          <section data-index={videos.length} data-testid="shorts-end" className="h-[100svh] snap-start grid place-items-center text-center bg-[#7B5CFA] text-white px-8">
            <div>
              <PartyPopper size={56} className="mx-auto mb-4 zn-wiggle" />
              <p className="font-display text-3xl sm:text-4xl font-bold mb-2">You watched them all!</p>
              <p className="opacity-90 mb-8">Want to make one of your own?</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button data-testid="shorts-replay-btn" onClick={() => goTo(0)} className="zn-btn bg-white text-[#1B1A2E]"><RotateCcw size={18} /> Watch again</button>
                <Link to="/kid/studio" data-testid="shorts-create-btn" className="zn-btn bg-[#FFD23F] text-[#1B1A2E]"><Clapperboard size={18} /> Make a video</Link>
              </div>
            </div>
          </section>
        </div>
      )}
      <ReportDialog target={report} onClose={() => setReport(null)} />
    </div>
  );
}
